import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '../../lib/utils';

interface PaginationProps {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function Pagination({ page, perPage, total, totalPages, onPageChange }: PaginationProps) {
  // Always render. Even at totalPages <= 1 we still want the row count
  // visible so users know what they're looking at; nav buttons just disable
  // themselves when there's nowhere to go.

  // Clamp once: callers occasionally pass an out-of-range page (e.g. mid-fetch
  // when total just shrunk). Without this the displayed range goes negative
  // or exceeds total.
  const safeTotalPages = Math.max(totalPages, 1);
  const safePage = Math.min(Math.max(page, 1), safeTotalPages);
  const start = total === 0 ? 0 : (safePage - 1) * perPage + 1;
  const end = Math.min(safePage * perPage, total);

  // Cursor contract:
  //   • idle + clickable → pointer
  //   • disabled → default arrow (NOT not-allowed)
  // Achieved by setting `cursor-pointer` on the button and overriding to
  // `cursor-default` only when `disabled`.
  // `rounded-md` on nav buttons matches the table wrapper, filter inputs,
  // and selects — keeps the system on one radius.
  const navBtn = cn(
    'inline-flex h-8 w-8 items-center justify-center rounded-md text-foreground',
    'cursor-pointer transition-all duration-150',
    'hover:bg-muted hover:text-primary',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:cursor-default disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-foreground',
  );

  return (
    <div className="flex items-center justify-end gap-4 px-1 text-xs text-muted-foreground">
      <span className="font-medium tabular-nums">
        {start}–{end} of {total}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(safePage - 1)}
          disabled={safePage <= 1}
          aria-label="Previous page"
          className={navBtn}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="min-w-[28px] px-1 text-center font-medium tabular-nums text-foreground">
          {safePage}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(safePage + 1)}
          disabled={safePage >= safeTotalPages}
          aria-label="Next page"
          className={navBtn}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
